if (!customElements.get('product-options')) {
    class ProductOptionPicker extends HTMLElement {
        connectedCallback() {
            this.section = this.closest('.product');
            if (!this.section || this.events) return;
            this.sectionId = this.section.dataset.sectionId;
            this.productUrl = this.dataset.productUrl;
            this.confirmedMarkup = this.innerHTML;
            this.events = new AbortController();
            this.addEventListener('click', event => {
                const option = event.target.closest('.product-option-picker__list-item');
                if (option && this.contains(option)) {
                    if (!option.classList.contains('disabled') && !option.classList.contains('chosen')) {
                        this.onClick(option);
                    }
                    this.closeDropdown(option.closest('.product-option-picker__dropdown'));
                    return;
                }
                const dropdown = event.target.closest('.product-option-picker__dropdown');
                if (dropdown && this.contains(dropdown)) {
                    dropdown.classList.toggle('active');
                }
            }, {signal: this.events.signal});
            this.addEventListener('keydown', event => {
                const option = event.target.closest('.product-option-picker__list-item');
                if (option && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    option.click();
                }
                if (event.key === 'Escape') {
                    this.querySelectorAll('.product-option-picker__dropdown').forEach(dropdown =>
                        this.closeDropdown(dropdown)
                    );
                }
            }, {signal: this.events.signal});
            document.addEventListener('click', event => {
                this.querySelectorAll('.product-option-picker__dropdown').forEach(dropdown => {
                    if (!dropdown.contains(event.target)) this.closeDropdown(dropdown);
                });
            }, {signal: this.events.signal});
        }

        disconnectedCallback() {
            this.request?.abort();
            this.events?.abort();
            this.events = null;
        }

        async onClick(option) {
            this.request?.abort();
            this.request = new AbortController();
            const request = this.request;
            const section = this.section;
            this.selectionPicker(option);
            section.querySelector('product-gallery')?.applySelectedColor();
            const optionProductUrl = option.querySelector('input')?.dataset.productUrl || this.productUrl;
            const chosenOptionValues = Array.from(this.querySelectorAll('ul[data-option-name]'))
                .map(list => list.querySelector('.chosen input')?.dataset.id)
                .filter(Boolean);
            const url = new URL(optionProductUrl, window.location.origin);
            url.searchParams.set('section_id', this.sectionId);
            url.searchParams.set('option_values', chosenOptionValues.join(','));
            section.setAttribute('aria-busy', 'true');
            try {
                const response = await fetch(url.toString(), {signal: request.signal});
                if (!response.ok) throw new Error('Variant response: ' + response.status);
                const text = await response.text();
                if (request.signal.aborted || !this.isConnected || this.request !== request) return;
                const parsed = new DOMParser().parseFromString(text, 'text/html');
                const incomingProduct = parsed.querySelector('.section-product') || parsed.querySelector('.product');
                if (!incomingProduct) throw new Error('Product section missing from variant response.');
                const variantId = incomingProduct.querySelector('input[name="id"]')?.value || '';
                const changedProduct = this.productUrl !== optionProductUrl;
                if (!variantId || changedProduct) {
                    section.innerHTML = incomingProduct.innerHTML;
                } else {
                    const required = ['.product-summary__product-form', 'product-options', '.product-summary__price'];
                    for (const selector of required) {
                        if (!incomingProduct.querySelector(selector) || !section.querySelector(selector)) {
                            throw new Error('Incomplete variant response: ' + selector);
                        }
                    }
                    const selectors = required.concat(['pickup-availability', '.product-summary__inventory-qty']);
                    for (const selector of selectors) {
                        const current = section.querySelector(selector);
                        const incoming = incomingProduct.querySelector(selector);
                        if (current && incoming) current.innerHTML = incoming.innerHTML;
                    }
                    this.confirmedMarkup = this.innerHTML;
                }
                section.querySelectorAll('.lazy-loading-image').forEach(container => {
                    if (typeof lazyloading === 'function') lazyloading(container);
                });
                section.querySelector('product-gallery')?.selectVariant(variantId);
                if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
                if (!window.Shopify?.designMode && section.classList.contains('main-product')) {
                    const newUrl = changedProduct
                        ? new URL(optionProductUrl, window.location.origin)
                        : new URL(window.location.href);
                    newUrl.searchParams.delete('option_values');
                    newUrl.searchParams.delete('section_id');
                    if (variantId) newUrl.searchParams.set('variant', variantId);
                    else newUrl.searchParams.delete('variant');
                    if (newUrl.href !== window.location.href) history.pushState({}, '', newUrl);
                }
                document.dispatchEvent(new CustomEvent('product:variantChange', {
                    detail: {id: variantId, sectionId: section.dataset.sectionId}
                }));
            } catch (error) {
                if (error.name === 'AbortError' || request.signal.aborted || !this.isConnected) return;
                this.innerHTML = this.confirmedMarkup;
                section.querySelector('product-gallery')?.applySelectedColor();
                console.error('Product variant could not be updated.', error);
            } finally {
                if (this.request === request) section.removeAttribute('aria-busy');
            }
        }

        selectionPicker(option) {
            const list = option.parentElement;
            for (const item of list.children) {
                const chosen = item === option;
                item.classList.toggle('chosen', chosen);
                const input = item.querySelector('input');
                if (input) input.checked = chosen;
            }
            list.dataset.optionValue = option.dataset.optionValue;
            const picker = option.closest('product-option-picker');
            picker.querySelectorAll('.product-option-picker-chosen > span, .product-option-picker-label--chosen')
                .forEach(label => { label.textContent = option.dataset.optionValue; });
            const currentSwatch = picker.querySelector('.product-option-picker-chosen > .product-option-swatch');
            const newSwatch = option.querySelector('.product-option-swatch');
            if (currentSwatch && newSwatch) {
                const color = getComputedStyle(newSwatch).getPropertyValue('--product-swatch-value') ||
                    option.dataset.optionValue;
                currentSwatch.innerHTML = newSwatch.innerHTML;
                currentSwatch.style.setProperty('--product-swatch-value', color);
            }
        }

        closeDropdown(dropdown) {
            dropdown?.classList.remove('active');
        }
    }
    customElements.define('product-options', ProductOptionPicker);
}
